import { useState, useEffect, useCallback } from "react";
import {
    X,
    ChevronLeft,
    ChevronRight,
    Heart,
    MessageCircle,
    Repeat,
    BarChart2,
    Bookmark,
    Share,
} from "lucide-react";
import { Media } from "../../../types/media";

interface TwitterOverlayProps {
    images: Media[];
    initialIndex: number;
    isOpen: boolean;
    onClose: () => void;
    post: any;
}

const TwitterOverlay: React.FC<TwitterOverlayProps> = ({
    images,
    initialIndex,
    isOpen,
    onClose,
    post,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const navigateNext = useCallback(() => {
        if (currentIndex < images.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, images.length]);

    const navigatePrev = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === "Escape") {
                onClose();
            } else if (e.key === "ArrowLeft") {
                navigatePrev();
            } else if (e.key === "ArrowRight") {
                navigateNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
        isOpen,
        currentIndex,
        images.length,
        onClose,
        navigateNext,
        navigatePrev,
    ]);

    // Reset current index when overlay opens
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            // Prevent body scrolling when overlay is open
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen, initialIndex]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex bg-black bg-opacity-90"
            onClick={onClose}
        >
            {/* Close button */}
            <button
                className="absolute top-4 left-4 z-10 p-2 text-white rounded-full hover:bg-gray-800"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            >
                <X className="w-5 h-5" />
            </button>

            {/* Main content */}
            <div
                className="flex w-full h-full"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Left navigation */}
                {images.length > 1 && currentIndex > 0 && (
                    <button
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 text-white rounded-full hover:bg-gray-800"
                        onClick={navigatePrev}
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                )}

                {/* Image container */}
                <div className="flex-1 flex items-center justify-center">
                    <img
                        src={
                            images[currentIndex].signedUrl || "/placeholder.svg"
                        }
                        alt={`${currentIndex + 1}`}
                        className="max-h-full max-w-full object-contain"
                    />
                </div>

                {/* Right sidebar with post info */}
                <div className="hidden md:block w-[350px] bg-black border-l border-gray-800 overflow-y-auto">
                    <div className="p-4">
                        {/* User info */}
                        <div className="flex items-start gap-3 mb-4">
                            <div className="flex-shrink-0">
                                <img
                                    src={post?.userImage || "/placeholder.svg"}
                                    alt="Profile"
                                    className="w-10 h-10 rounded-full object-cover"
                                />
                            </div>
                            <div>
                                <div className="flex items-center gap-1">
                                    <span className="font-bold text-white truncate">
                                        {post?.displayName}
                                    </span>
                                    <span className="text-gray-500 truncate">
                                        @{post?.username}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Post content */}
                        <p className="text-white mb-4 whitespace-pre-line">
                            {post?.content}
                        </p>

                        {/* Post date */}
                        <div className="text-gray-500 text-sm mb-4">
                            {(post?.type === 'Lead Generation' || (post?.type === 'preview' && post?.background && post?.textColor)) ? (
                                // Show date and time for Lead Generation posts (like Facebook format)
                                (() => {
                                     const scheduledFor = post?.scheduledFor;
                                    const date = new Date(scheduledFor);
                                    const dateString = date.toLocaleDateString("en-US", {
                                        month: "long",
                                        day: "numeric",
                                    });
                                    const timeString = date.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                    });
                                    return `${dateString} at ${timeString}`;
                                })()
                            ) : (
                                // Show full date and time for other posts
                                <>
                                    {new Date(
                                        post?.scheduledFor || Date.now()
                                    ).toLocaleString("en-US", {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                    })}{" "}
                                    ·{" "}
                                    {new Date(
                                        post?.scheduledFor || Date.now()
                                    ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                    })}
                                </>
                            )}
                            {post?.views && <span> · {post.views} views</span>}
                        </div>

                        {/* Engagement stats */}
                        <div className="border-t border-b border-gray-800 py-2 my-2">
                            <div className="flex items-center justify-between text-gray-400">
                                <div className="flex items-center gap-1">
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{10}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Repeat className="w-4 h-4" />
                                    <span>{3}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Heart className="w-4 h-4" />
                                    <span>{132}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <BarChart2 className="w-4 h-4" />
                                    <span>{560}</span>
                                </div>
                                <div className="flex items-center">
                                    <Bookmark className="w-4 h-4" />
                                </div>
                                <div className="flex items-center">
                                    <Share className="w-4 h-4" />
                                </div>
                            </div>
                        </div>

                        {/* Reply section placeholder */}
                        <div className="flex items-center gap-3 mt-4">
                            <div className="w-10 h-10 rounded-full bg-gray-800"></div>
                            <div className="text-gray-500">Post your reply</div>
                        </div>
                    </div>
                </div>

                {/* Right navigation */}
                {images.length > 1 && currentIndex < images.length - 1 && (
                    <button
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 text-white rounded-full hover:bg-gray-800"
                        onClick={navigateNext}
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default TwitterOverlay;
